/**
 * Constructor PURO de opciones para la Jitsi External API (F3-G). Separa la
 * lógica (testeable sin DOM) del componente que hace `new JitsiMeetExternalAPI`.
 *
 * El servidor resuelve el `domain` + `room` (sala determinista con salt de
 * servidor); aquí solo se ensamblan las opciones del iframe: prejoin OFF,
 * displayName del participante, y una config conservadora.
 */

/** Entrada para construir las opciones del iframe embebido. */
export interface JitsiEmbedInput {
  /** Sala determinista resuelta por el servidor. */
  roomName: string;
  /** Nombre a mostrar del participante. */
  displayName?: string | null;
  /** Sobrescribe `prejoinPageEnabled` (default: false = sin prejoin). */
  prejoin?: boolean;
}

/** Opciones (sin `parentNode`) para `new JitsiMeetExternalAPI(domain, options)`. */
export interface JitsiEmbedOptions {
  roomName: string;
  userInfo?: { displayName: string };
  configOverwrite: {
    prejoinPageEnabled: boolean;
    startWithAudioMuted: boolean;
    disableDeepLinking: boolean;
  };
  interfaceConfigOverwrite: {
    MOBILE_APP_PROMO: boolean;
  };
}

/**
 * Ensambla las opciones del iframe Jitsi. Puro: no toca el DOM ni la red.
 * `prejoin` off por defecto (el usuario ya pasó el pre-join de la app).
 */
export function buildJitsiEmbedOptions(input: JitsiEmbedInput): JitsiEmbedOptions {
  const displayName = input.displayName?.trim();
  return {
    roomName: input.roomName,
    ...(displayName ? { userInfo: { displayName } } : {}),
    configOverwrite: {
      prejoinPageEnabled: input.prejoin ?? false,
      startWithAudioMuted: true,
      disableDeepLinking: true,
    },
    interfaceConfigOverwrite: {
      MOBILE_APP_PROMO: false,
    },
  };
}

/** URL del script External API de un dominio Jitsi (`https://<domain>/external_api.js`). */
export function jitsiExternalApiUrl(domain: string): string {
  const clean = domain
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");
  return `https://${clean}/external_api.js`;
}
